import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const fetchIndentData = createAsyncThunk(
  'indent/fetchData',
  async ({ role, enteredBy }, { rejectWithValue }) => {
    try {
      const endpoint = role === "admin" 
        ? `${API_BASE_URL}/adminpage/indent`
        : `${API_BASE_URL}/indent/indent`;

      const response = await axios.get(endpoint, {
        params: {
          ...(role !== "admin" && { ProjectIncharge: enteredBy }),
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchDropdownOptions = createAsyncThunk(
  'indent/fetchDropdownOptions',
  async (_, { rejectWithValue }) => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      return {
        users: usersRes.data.map(user => ({ id: user.UserID, name: user.Username })),
        projects: projectsRes.data.map(project => ({ id: project.ProjectID, name: project.ProjectName })),
        stages: stagesRes.data.map(stage => ({ id: stage.StageID, name: stage.StageName })),
        types: typesRes.data.map(type => ({ id: type.TypeID, name: type.TypeName })),
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const indentSlice = createSlice({
  name: 'indent',
  initialState: {
    indents: [],
    filteredIndents: [],
    dropdownData: {
      users: [],
      projects: [],
      stages: [],
      types: [],
    },
    selectedRow: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    setSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    },
    filterIndents: (state, action) => {
      console.log('Filter action payload:', action.payload); // Debug log
      
      if (!action.payload) {
        state.filteredIndents = state.indents;
        return;
      }
      
      if (Array.isArray(action.payload)) {
        state.filteredIndents = action.payload;
        return;
      }
      
      const query = action.payload.toString().toLowerCase().trim();
      if (!query) {
        state.filteredIndents = state.indents;
        return;
      }
      
      state.filteredIndents = state.indents.filter(indent => {
        const searchableValues = [
          indent.IndentID?.toString(),
          indent.Status,
          indent.ProjectName,
          indent.TypeName,
          indent.CreationDate?.toString(),
          indent.TaskSchedule
        ];
        
        return searchableValues.some(value => 
          value && value.toString().toLowerCase().includes(query)
        );
      });
      
      console.log('Filtered indents count:', state.filteredIndents.length); // Debug log
    },
    filterByDate: (state, action) => {
      const { dateFrom, dateTo } = action.payload;
      if (!dateFrom || !dateTo) {
        state.filteredIndents = state.indents;
        return;
      }

      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date

      state.filteredIndents = state.indents
        .filter(indent => {
          const creationDate = new Date(indent.CreationDate);
          return creationDate >= fromDate && creationDate <= toDate;
        })
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIndentData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndentData.fulfilled, (state, action) => {
        const formattedIndents = action.payload
          .map(indent => {
            // Parse and reformat dates to ISO format
            const parseDate = (dateStr) => {
              if (!dateStr) return null;
              // If it's already in ISO format, return as is
              if (dateStr.includes('T')) return dateStr;
              // If it's in dd/MM/yy format, convert to ISO
              const [day, month, year] = dateStr.split('/');
              if (day && month && year) {
                const fullYear = year.length === 2 ? '20' + year : year;
                return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
              }
              return null;
            };

            return {
            ...indent,
            L1: indent.L1 ? "Approved" : "Pending",
            L2: indent.L2 ? "Approved" : "Pending",
            PO: indent.PO ? "Approved" : "Pending",
            PH: indent.PH ? "Approved" : "Pending",
            IR: indent.IR ? "Approved" : "Pending",
              CreationDate: parseDate(indent.CreationDate) || "N/A",
              PEApprovalDate: parseDate(indent.PEApprovalDate) || "N/A",
              L1ApprovedDate: parseDate(indent.L1ApprovedDate) || "N/A",
              L2ApprovalDate: parseDate(indent.L2ApprovalDate) || "N/A"
            };
          })
          .sort((a, b) => {
            const dateA = new Date(a.CreationDate);
            const dateB = new Date(b.CreationDate);
            return isNaN(dateB.getTime()) - isNaN(dateA.getTime()) || dateB - dateA;
          });
        
        state.indents = formattedIndents;
        state.filteredIndents = formattedIndents;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchIndentData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch indents';
        state.indents = [];
        state.filteredIndents = [];
      })
      .addCase(fetchDropdownOptions.fulfilled, (state, action) => {
        state.dropdownData = action.payload;
      });
  },
});

export const { setSelectedRow, filterIndents, filterByDate } = indentSlice.actions;
export default indentSlice.reducer;